// Lightweight, first-party A/B testing.
//
// No third-party script, no cookies shared across sites: the visitor is
// assigned a variant once (persisted in localStorage so the experience stays
// stable across pages and repeat visits) and a single "exposure" is recorded
// server-side per session. Clicks and leads carry the variant so the admin
// report can compute conversion rate per variant.

export const WHATSAPP_EXPERIMENT = "floating_whatsapp_v1";

export type WhatsAppVariant = "control" | "labeled" | "left";

export const WHATSAPP_VARIANTS: WhatsAppVariant[] = [
  "control", // icon-only green circle, bottom-right (baseline)
  "labeled", // green pill with icon + "WhatsApp Me", bottom-right
  "left", // icon-only circle with chat icon, bottom-left
];

export const WHATSAPP_VARIANT_LABELS: Record<WhatsAppVariant, string> = {
  control: "Icon only — bottom right",
  labeled: 'Pill with "WhatsApp Me" — bottom right',
  left: "Chat icon — bottom left",
};

const STORAGE_KEY = `akg:ab:${WHATSAPP_EXPERIMENT}`;

function isVariant(v: string | null): v is WhatsAppVariant {
  return !!v && (WHATSAPP_VARIANTS as string[]).includes(v);
}

/**
 * Sticky variant for this browser. Returns null on the server so SSR markup
 * stays deterministic — the component assigns after hydration.
 */
export function getAssignedVariant(): WhatsAppVariant | null {
  if (typeof window === "undefined") return null;

  // Manual override for QA: ?ab=labeled
  try {
    const forced = new URLSearchParams(window.location.search).get("ab");
    if (isVariant(forced)) {
      window.localStorage.setItem(STORAGE_KEY, forced);
      return forced;
    }
  } catch {
    /* ignore */
  }

  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (isVariant(stored)) return stored;
    const picked =
      WHATSAPP_VARIANTS[Math.floor(Math.random() * WHATSAPP_VARIANTS.length)]!;
    window.localStorage.setItem(STORAGE_KEY, picked);
    return picked;
  } catch {
    // Storage blocked (private mode): still run the test, just not sticky.
    return WHATSAPP_VARIANTS[
      Math.floor(Math.random() * WHATSAPP_VARIANTS.length)
    ]!;
  }
}

/** Variant already assigned in this browser, for tagging clicks and leads. */
export function currentVariant(): string {
  if (typeof window === "undefined") return "";
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isVariant(stored) ? stored : "";
  } catch {
    return "";
  }
}

const EXPOSED_KEY = `${STORAGE_KEY}:exposed`;

/**
 * Record that this session saw the variant. Once per tab session — the
 * denominator of the conversion rate.
 */
export function recordExposure(variant: WhatsAppVariant, sessionId: string) {
  if (typeof window === "undefined") return;
  try {
    if (window.sessionStorage.getItem(EXPOSED_KEY) === variant) return;
    window.sessionStorage.setItem(EXPOSED_KEY, variant);
  } catch {
    /* storage blocked — send anyway, the server dedupes on session id */
  }

  const payload = JSON.stringify({
    experiment: WHATSAPP_EXPERIMENT,
    variant,
    sessionId: sessionId || undefined,
    path: window.location.pathname,
  });

  try {
    if (typeof navigator.sendBeacon === "function") {
      navigator.sendBeacon(
        "/api/public/ab-exposure",
        new Blob([payload], { type: "application/json" }),
      );
      return;
    }
    void fetch("/api/public/ab-exposure", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: payload,
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    /* never break rendering for an experiment */
  }
}
